import { clsx } from "clsx";
import type { CSSProperties, ReactNode } from "react";
import type { PatternType, SectionBackground, SectionPaddingSize } from "../../types/styling";
import { isDarkBackground } from "../../types/styling";
import { getSectionBackgroundClass } from "../../utils/styling";
import { Flex, type FlexProps } from "../Flex/Flex";
import "./Section.css";

export type AnimationType = "fadeInUp" | "fadeIn" | "scaleIn";

export interface SectionProps {
  /** Section content */
  children: ReactNode;
  /** Background color - semantic base/semantic colors or gradient names with full type safety */
  background?: SectionBackground;
  /** Section padding size - responsive presets */
  padding?: SectionPaddingSize;
  /** Pattern overlay type - subtle grid pattern on top of background */
  pattern?: PatternType;
  /** Container max width - overrides the `--section-max-w` token default (1200px) */
  containerWidth?: string;
  /** Additional custom classes for the section element */
  className?: string;
  /** Additional custom classes for the container div */
  containerClassName?: string;
  /**
   * Render the container through the Flex layout primitive (ADR-0094) with
   * these layout props. Absent = the plain container div (unchanged).
   */
  containerLayout?: Pick<FlexProps, "justify" | "align" | "gap" | "direction" | "wrap">;
  /**
   * Inline style passthrough on the root element (ADR-0091 kit-adoption
   * escape hatch — interim for phase 2, carries a migrated kit section's
   * dynamic `style={{ background }}`; phase 3 replaces this with a `tone` /
   * `--section-overlay` token).
   */
  style?: CSSProperties;
  /** Section ID for anchor links */
  id?: string;
  /** Element type to render as */
  as?: "section" | "div" | "article" | "main" | "aside" | "header" | "nav" | "footer";
  /** Scroll-triggered animation type */
  animate?: AnimationType;
  /** Extra data attributes to spread on the section element */
  [key: `data-${string}`]: string | number | undefined;
}

/**
 * Section - Base container component for all page sections
 *
 * Provides consistent structure and styling for sections throughout the app:
 * - Type-safe background colors (base colors, semantic colors, gradients)
 * - Responsive padding presets
 * - Standard container with max-width
 * - Semantic HTML support
 *
 * This component eliminates duplication of the common pattern:
 * ```tsx
 * <section className="ds-section--py-lg bg-surface">
 *   <div className="ds-section-container">
 *     {children}
 *   </div>
 * </section>
 * ```
 *
 * @example Basic usage
 * ```tsx
 * <Section background="base-100" padding="lg">
 *   <SectionHeader title="Features" subtitle="Why choose us" />
 *   {content}
 * </Section>
 * ```
 *
 * @example With gradient background
 * ```tsx
 * <Section background="subtle-purple" padding="xl">
 *   <h2>Hero Section</h2>
 * </Section>
 * ```
 *
 * @example With custom container width
 * ```tsx
 * <Section background="base-200" containerWidth="max-w-7xl">
 *   <div>Wide content</div>
 * </Section>
 * ```
 *
 * @example As a different element
 * ```tsx
 * <Section as="article" id="blog-post" background="base-100">
 *   <BlogPost />
 * </Section>
 * ```
 */
export function Section({
  children,
  background = "base-100",
  padding = "lg",
  pattern,
  containerWidth,
  className,
  containerClassName,
  containerLayout,
  style,
  id,
  as: Component = "section",
  animate,
  ...rest
}: SectionProps) {
  // "none" (ADR-0091): a migrated kit-adoption root owns its own background
  // (className texture / inline style) — <Section> must not fight it with an
  // auto light/dark text-color class.
  const textColorClass =
    background === "none" ? null : isDarkBackground(background) ? "section-dark" : "section-light";

  const sectionClasses = clsx(
    padding !== "none" && `ds-section--py-${padding}`,
    getSectionBackgroundClass(background),
    textColorClass,
    pattern === "cross" && "pattern-cross",
    pattern === "dots" && "pattern-dots",
    pattern === "diagonal" && "pattern-diagonal",
    pattern === "hexagon" && "pattern-hexagon",
    id && "scroll-mt-20",
    className,
  );

  const containerClasses = clsx("ds-section-container", containerWidth, containerClassName);

  return (
    <Component
      id={id}
      className={sectionClasses}
      style={style}
      data-component="Section"
      // ⚠️ Never pass `animate` on a section that hosts a fixed overlay reading
      // the --z-index-* scale — every [data-animate] value starts at
      // opacity: 0 (styles/global.css), which creates a stacking context
      // that traps the overlay's z-index inside this section; fadeInUp/
      // scaleIn's transform additionally makes this element a containing
      // block for position: fixed, breaking a full-viewport inset: 0.
      // docs/specs/website/design-token-linting.md "The data-animate landmine".
      {...(animate ? { "data-animate": animate } : {})}
      {...rest}
    >
      {containerLayout ? (
        <Flex className={containerClasses} {...containerLayout}>
          {children}
        </Flex>
      ) : (
        <div className={containerClasses}>{children}</div>
      )}
    </Component>
  );
}

Section.displayName = "Section";
